import React, { useLayoutEffect, useState } from "react";
import { H2, Subtitle1 } from "melodies-source/Text";
import { useMobileMediaQuery } from "hooks/useMobileMediaQuery";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { FlexColumn } from "Components/Flex";

interface BaseTab {
  label: string | React.ReactNode;
  content: React.ReactNode;
  icon?: React.ReactNode;
  hasHorizontalPadding?: boolean;
}

interface TabWithKey extends BaseTab {
  key: string;
}

interface TabWithParam extends BaseTab {
  param: string;
}

export type Tab = TabWithKey | TabWithParam;

interface Props {
  tabs: Tab[];
  basePath?: string;
}

const hasParam = (tab: Tab): tab is TabWithParam => {
  return "param" in tab;
};

export const Tabs: React.FC<Props> = ({ tabs, basePath, ...props }) => {
  const [activeTab, setActiveTab] = useState<Tab>(tabs[0]);
  const { tabParam } = useParams<{ tabParam: string }>();
  const isMobile = useMobileMediaQuery();
  const history = useHistory();

  const getTabId = (tab: Tab) => (hasParam(tab) ? tab.param : tab.key);

  const handleTabClick = (tab: Tab) => {
    hasParam(tab)
      ? history.replace(`${basePath || ""}/${tab.param}`)
      : setActiveTab(tab);
  };

  useLayoutEffect(() => {
    if (!basePath) return;

    const tab = tabs.find((tab) => hasParam(tab) && tab.param === tabParam);
    if (tab) {
      setActiveTab(tab);
    }
  }, [basePath, tabParam]);

  const TabLabel = isMobile ? Subtitle1 : H2;
  return (
    <TabsWrapper {...props}>
      <TabContainer>
        {tabs.map((tab) => (
          <Tab
            key={getTabId(tab)}
            selected={getTabId(tab) === getTabId(activeTab)}
            onClick={() => handleTabClick(tab)}
          >
            {tab.icon && <TabIcon>{tab.icon}</TabIcon>}
            <TabLabel
              style={
                !hasParam(tab)
                  ? isMobile
                    ? { fontWeight: 500 }
                    : { fontSize: "24px", fontWeight: 500 }
                  : undefined
              }
            >
              {tab.label}
            </TabLabel>
          </Tab>
        ))}
      </TabContainer>
      <TabContent
        hasHorizontalPadding={
          activeTab?.hasHorizontalPadding !== undefined
            ? activeTab.hasHorizontalPadding
            : true
        }
      >
        {activeTab?.content}
      </TabContent>
    </TabsWrapper>
  );
};

interface ContentProps {
  hasHorizontalPadding: boolean;
}

export const TabsWrapper = styled.div`
  display: flex;
  flex-flow: column;
  flex-grow: 1;
`;

export const TabContent = styled(FlexColumn)<ContentProps>`
  padding: ${({ hasHorizontalPadding }) =>
    `20px${hasHorizontalPadding ? "" : " 0px"}`};
  background-color: var(--component-background-color);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.16);
  border-radius: 0 0 12px 12px;
  z-index: 1;
  flex-grow: 1;

  ${({ theme }) => theme.media.mobile} {
    padding: ${({ hasHorizontalPadding }) =>
      `20px${hasHorizontalPadding ? " 15px" : " 0px"}`};
  }
`;

const Tab = styled.div<{ selected?: boolean }>`
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: var(--component-background-color);
  height: 80px;
  cursor: ${({ selected }) => (selected ? "default" : "pointer")};
  text-align: center;

  ${({ selected }) => !selected && "opacity: 0.6"};

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    justify-content: center;
    height: 60px;
  }
`;

const TabContainer = styled.div`
  display: flex;
  gap: 2px;
  z-index: 2;

  & > div:first-child {
    border-top-left-radius: 12px 12px;
  }

  & > div:last-child {
    border-top-right-radius: 12px 12px;
  }

  svg {
    margin-right: 5px;
    ${({ theme }) => theme.media.mobile} {
      margin-right: 0px;
      transform: scale(0.8);
    }
  }
`;

const TabIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 30px;
  color: var(--secondary-color);
`;
